/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/th';
import { maxBy } from 'lodash';

import { useTranslation } from 'react-i18next';

// ui
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import DateFnsUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';

// HighCharts
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

// init highcharts module
HighchartsExport(Highcharts);
HighchartsExportData(Highcharts);

export default function WaterGateChart(props) {
  const { t, i18n } = useTranslation();
  const { id, provinceId } = props;
  const today = moment(new Date()).locale('th').format('YYYY-MM-DD');

  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(true);

  const [dataStation, setDataStation] = useState([]);
  const [dataProvince, setDataProvince] = useState([]);
  const [wgId, setWgId] = useState(id);
  const [stateProvinceId, setStateProvinceId] = useState(provinceId);
  const [startDate, setStartDate] = useState(moment().subtract(3, 'days').locale('th').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment(new Date()).locale('th').format('YYYY-MM-DD'));
  const [chartOption, setChartOptions] = useState();
  const [getMax, setMax] = useState();

  function getChartOptions() {
    const options = {
      title: {
        text: '',
      },
      plotOptions: {
        line: {
          turboThreshold: 0,
        },
      },
      xAxis: {
        type: 'datetime',
        labels: {
          format: '{value:%d-%b<br />%H:%M}',
        },
      },
      yAxis: {
        // Primary yAxis
        title: {
          text: `${t('ระดับน้ำ')} (${t('ม.รทก.')})`,
        },
        labels: {
          format: '{value:.2f}',
        },
        minorGridLineWidth: 0,
        gridLineWidth: 0,
        alternateGridColor: null,
        plotLines: [
          {
            value: null,
            color: 'red',
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
          {
            value: null,
            color: 'black',
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
        ],
      },
      legend: {
        enabled: true,
      },
      tooltip: {
        crosshairs: true,
        shared: true,
        valueSuffix: ` ${t('ม.รทก.')} `,
      },
      series: [
        {
          name: t('ระดับน้ำเหนือ'),
          data: [],
        },
        {
          name: t('ระดับน้ำท้าย'),
          data: [],
        },
      ],
    };

    return options;
  }

  function renderChart() {
    const url = `${process.env.REACT_APP_API_URL}/public/watergate_graph?station_id=${wgId}&start_date=${startDate}&end_date=${endDate}`;
    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return 'Something went wrong.';
      })
      .then((result) => {
        // transform datas
        const dataInSeries = [];
        const dataOutSeries = [];
        const dataIn = result.data[0].data;
        const dataOut = result.data[1].data;
        const groundLevel = result.data[0].data.ground_level;
        const criticallevel = result.data[0].data.critical_level;
        let ts = null;
        let val = null;
        // find max, min
        const max = maxBy(dataIn, 'value') === undefined ? 0 : maxBy(dataIn, 'value').value;
        setMax(max);
        dataIn.map((n) => {
          val = parseFloat(n.value);
          ts = moment(n.datetime, 'YYYY/MM/DD hh:mm:ss').add(7, 'hours').valueOf();
          dataInSeries.push([ts, val]);
          return dataInSeries;
        });
        dataOut.map((n) => {
          val = parseFloat(n.value);
          ts = moment(n.datetime, 'YYYY/MM/DD hh:mm:ss').add(7, 'hours').valueOf();
          dataOutSeries.push([ts, val]);
          return dataOutSeries;
        });
        // set chart options
        const chartOptions = getChartOptions();
        chartOptions.series[0].data = dataInSeries;
        chartOptions.series[1].data = dataOutSeries;
        chartOptions.yAxis.min = groundLevel;
        // plotLines
        if (criticallevel) {
          chartOptions.yAxis.plotLines[0].value = criticallevel;
          chartOptions.yAxis.plotLines[0].label.text = `${t('ค่าระดับแถบเตือนภัยสูงสุด')} <strong>${parseFloat(criticallevel).toFixed(2)}</strong> ม.รทก.`;
        }
        if (groundLevel) {
          chartOptions.yAxis.plotLines[1].value = groundLevel;
          chartOptions.yAxis.plotLines[1].label.text = `${t('ระดับท้องน้ำ')} <strong>${parseFloat(groundLevel).toFixed(2)}</strong> ม.รทก.`;
        }
        setChartOptions(chartOptions);
      })
      .catch((err) => setError(err));
  }

  function getStation() {
    fetch(`${process.env.REACT_APP_API_URL}/frontend/shared/watergate_station?province_code=${stateProvinceId}`)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Something went wrong ...');
      })
      .then((result) => {
        setDataStation(result.data);
      })
      .catch((err) => setError(err));
  }

  const handleProvinceChange = (event) => {
    setStateProvinceId(event.target.value);
  };

  const handleStationChange = (event) => {
    setWgId(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleEndDateChange = (date) => {
    setEndDate(moment(date).format('YYYY-MM-DD'));
  };

  useEffect(() => {
    function fetchData() {
      fetch(`${process.env.REACT_APP_API_URL}/public/watergate_load`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Something went wrong ...');
        })
        .then((result) => {
          const provinces = result.province.data.sort((a, b) => a.province_name.th.localeCompare(b.province_name.th, 'th'));
          setDataProvince(provinces);
        })
        .catch((err) => setError(err));
      getStation();
      renderChart();
      setLoading(false);
    }
    fetchData();
  }, []);

  useEffect(() => {
    getStation();
  }, [stateProvinceId]);

  useEffect(() => {
    renderChart();
  }, [wgId, endDate]);

  return (
    <>
      {error && <div>Error Occured</div>}
      {isLoading && (
      <div className="text-center">
        <FontAwesomeIcon icon={faSpinner} size="3x" />
      </div>
      )}
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl>
                <InputLabel shrink htmlFor="province-label-placeholder">
                  {t('จังหวัด')}
                </InputLabel>
                <Select id="watergate-select-province" value={stateProvinceId} onChange={handleProvinceChange}>
                  {dataProvince.map((row) => (
                    <MenuItem key={row.province_code} value={row.province_code}>
                      {i18n.language === 'en' ? row.province_name.en : row.province_name.th}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={9}>
              <FormControl>
                <InputLabel shrink htmlFor="station-label-placeholder">
                  {t('สถานี')}
                </InputLabel>
                <Select id="watergate-select-id" value={wgId} onChange={handleStationChange}>
                  {dataStation.map((row) => (
                    <MenuItem key={row.station_id} value={row.station_id}>
                      {row.station_name.th}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    id="watergate-start-date"
                    label={t('วันที่เริ่มต้น')}
                    format="YYYY-MM-DD"
                    value={startDate}
                    maxDate={today}
                    onChange={handleStartDateChange}
                  />
                </MuiPickersUtilsProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    id="watergate-end-date"
                    label={t('วันที่สิ้นสุด')}
                    format="YYYY-MM-DD"
                    value={endDate}
                    maxDate={today}
                    onChange={handleEndDateChange}
                  />
                </MuiPickersUtilsProvider>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl>
            <Button id="watergate-chart-btn" variant="outlined" color="primary" size="small" onClick={renderChart} title={t('แสดงกราฟ')}>
            {t('แสดง')}
            </Button>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          {
            getMax === 0 ? (
              <Paper>{t('ไม่มีข้อมูลในช่วงเวลาที่เลือก')}</Paper>
            ) : (
              <HighchartsReact highcharts={Highcharts} options={chartOption} />
            )
          }
        </Grid>
      </Grid>
    </>
  );
}

WaterGateChart.propTypes = {
  id: PropTypes.number.isRequired,
  provinceId: PropTypes.string.isRequired,
};
